import { useState, Fragment, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Info, ChevronDown, ChevronRight, Search, X, Filter, RefreshCw, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanningFinishedGoodsPage = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Data states
  const [salesOrders, setSalesOrders] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [inventoryLevels, setInventoryLevels] = useState([]);
  const [finishedGoodsData, setFinishedGoodsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStockStatus, setSelectedStockStatus] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [hideZeroRows, setHideZeroRows] = useState(false);
  const [actualCategories, setActualCategories] = useState([]);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [bomSyncLoading, setBomSyncLoading] = useState(false);

  // Fetch live data and calculate projections
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch sales orders, work orders, and inventory levels
        const [salesOrdersResponse, workOrdersResponse, inventoryResponse] = await Promise.all([
          axios.get(`${API}/sales-orders`),
          axios.get(`${API}/work-orders`),
          axios.get(`${API}/items`)
        ]);

        const salesOrdersData = salesOrdersResponse.data;
        const workOrdersData = workOrdersResponse.data;
        const inventoryData = inventoryResponse.data;

        setSalesOrders(salesOrdersData);
        setWorkOrders(workOrdersData);
        setInventoryLevels(inventoryData);

        // Calculate projections for finished goods
        const calculatedData = calculateFinishedGoodsProjections(salesOrdersData, workOrdersData, inventoryData);
        setFinishedGoodsData(calculatedData);

        // Extract categories from finished goods data
        const categories = [...new Set(calculatedData.map(item => item.category))].filter(Boolean);
        setActualCategories(categories);

      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again.");
        toast.error("Failed to load planning data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // WebSocket listener for sales orders updates
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'sales_orders_updated') {
          // Clear the timeout if it exists
          if (window.salesOrdersSyncTimeout) {
            clearTimeout(window.salesOrdersSyncTimeout);
            window.salesOrdersSyncTimeout = null;
          }

          // Refetch data when sales orders are updated
          const fetchData = async () => {
            try {
              const [salesOrdersResponse, workOrdersResponse, inventoryResponse] = await Promise.all([
                axios.get(`${API}/sales-orders`),
                axios.get(`${API}/work-orders`),
                axios.get(`${API}/items`)
              ]);

              const salesOrdersData = salesOrdersResponse.data;
              const workOrdersData = workOrdersResponse.data;
              const inventoryData = inventoryResponse.data;

              setSalesOrders(salesOrdersData);
              setWorkOrders(workOrdersData);
              setInventoryLevels(inventoryData);

              // Recalculate projections
              const calculatedData = calculateFinishedGoodsProjections(salesOrdersData, workOrdersData, inventoryData);
              setFinishedGoodsData(calculatedData);

              // Update categories
              const categories = [...new Set(calculatedData.map(item => item.category))].filter(Boolean);
              setActualCategories(categories);

              // Reset sync loading state
              setSyncLoading(false);
              toast.success("Sales orders synced successfully");

            } catch (error) {
              console.error("Error refetching data after sales orders update:", error);
              setSyncLoading(false);
            }
          };

          fetchData();
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    // Add event listener to window for WebSocket messages
    window.addEventListener('websocket-message', handleWebSocketMessage);

    return () => {
      window.removeEventListener('websocket-message', handleWebSocketMessage);
    };
  }, []);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openDetailModal = (product) => {
    setSelectedProduct(product);
    setShowDetailModal(true);
  };

  // Calculate weeks on hand based on inventory and average weekly shipments
  const calculateWeeksOnHand = (inventory, avgWeeklyShipments) => {
    if (avgWeeklyShipments === 0) return 999;
    return Math.max(0, inventory / avgWeeklyShipments);
  };

  // Generate week headers as "Week Of" dates (Monday of each week)
  const getWeekHeaders = () => {
    // Use the same planning horizon start as the calculation
    const planningStart = getPlanningHorizonStart();

    // Generate 13 week headers
    const weeks = [];
    for (let i = 0; i < 13; i++) {
      const weekDate = new Date(planningStart);
      weekDate.setDate(planningStart.getDate() + (i * 7));
      const month = weekDate.getMonth() + 1;
      const day = weekDate.getDate();
      weeks.push(`${month}/${day}`);
    }
    return weeks;
  };

  // Get the current week Monday for forward-looking planning
  const getPlanningHorizonStart = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Calculate the Monday of the current week
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - daysToSubtract);

    return currentMonday;
  };

  const weekHeaders = getWeekHeaders();

  // Calculate finished goods projections from live data
  const calculateFinishedGoodsProjections = (salesOrdersData, workOrdersData, inventoryData) => {
    // 1. Use Inventory/Items data as the source of truth for the list of products
    if (!inventoryData || inventoryData.length === 0) return [];

    // 2. Filter: Only show "Assembly Items" (Finished Goods)
    const assemblyItemsOnly = inventoryData.filter(item => 
      item.itemtype === 'Assembly' || 
      item.item_type_formatted === 'Assembly' ||
      item.is_assembly_item === true
    );

    // Calculate Planning Horizon Start (Monday of current week)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon...
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const planningStart = new Date(today);
    planningStart.setDate(today.getDate() - daysToSubtract);

    // 3. Pre-process Sales Orders (Shipments) into a Map by SKU
    // Structure: { [sku]: { [weekIndex]: { qty: 0, details: [] } } }
    const salesMap = {};
    if (salesOrdersData) {
      salesOrdersData.forEach(order => {
        if (!order.items) return;
        order.items.forEach(lineItem => {
          const sku = lineItem.sku;
          const dateStr = lineItem.expected_date || order.ship_date;
          if (!dateStr) return;

          const date = new Date(dateStr);
          const diffTime = date.getTime() - planningStart.getTime();
          const weekIndex = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));

          // Initialize map entry if missing
          if (!salesMap[sku]) salesMap[sku] = { weeks: {}, warnings: [] };

          // --- WARNING LOGIC START ---
          // Check for missing or past due dates on this line item
          const today = new Date();
          today.setHours(0,0,0,0);
          
          if (!dateStr) {
             salesMap[sku].warnings.push(`${order.order_number}: No Ship Date`);
          } else {
             const checkDate = new Date(dateStr);
             // Fix timezone for accurate comparison
             checkDate.setMinutes(checkDate.getMinutes() + checkDate.getTimezoneOffset());
             
             if (checkDate < today && (lineItem.quantity_remaining || 0) > 0) {
                salesMap[sku].warnings.push(`${order.order_number}: Past Due (${dateStr})`);
             }
          }
          // --- WARNING LOGIC END ---

          if (weekIndex >= 0 && weekIndex < 13) {
            if (!salesMap[sku].weeks[weekIndex]) salesMap[sku].weeks[weekIndex] = { qty: 0, details: [] };

            salesMap[sku].weeks[weekIndex].qty += (lineItem.quantity_remaining || 0);
            salesMap[sku][weekIndex].details.push({
              ref: order.order_number || 'SO',
              qty: lineItem.quantity_remaining,
              customer: order.customer_name,
              date: dateStr // Add actual date
            });
          }
        });
      });
    }

    // 4. Pre-process Work Orders (Production) into a Map by Item ID
    // Structure: { [itemId]: { [weekIndex]: { qty: 0, details: [] } } }
    const productionMap = {};
    if (workOrdersData) {
      workOrdersData.forEach(wo => {
        if (!wo.planned_date) return;
        
        const date = new Date(wo.planned_date);
        const diffTime = date.getTime() - planningStart.getTime();
        const weekIndex = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));

        if (weekIndex >= 0 && weekIndex < 13) {
          const itemId = wo.item_id;
          if (itemId) {
            if (!productionMap[itemId]) productionMap[itemId] = {};
            if (!productionMap[itemId][weekIndex]) productionMap[itemId][weekIndex] = { qty: 0, details: [] };

            productionMap[itemId][weekIndex].qty += (wo.planned_quantity || 0);
            productionMap[itemId][weekIndex].details.push({
              ref: wo.wo_number,
              qty: wo.planned_quantity,
              line: wo.production_line,
              date: wo.planned_date // Add actual date
            });
          }
        }
      });
    }

    // 5. Build the rows for every Assembly Item
    return assemblyItemsOnly.map((item, index) => {
      const itemId = item.id;
      const sku = item.custom_sku || item.itemid || item.displayname;
      const name = item.displayname || item.itemid || sku;
      const category = item.product_group || item.costcategoryname || 'Finished Goods';
      
      // Calculate Avg Weekly Shipments based on the sales data found
      const salesEntry = salesMap[sku] || salesMap[name] || salesMap[item.itemid] || { weeks: {}, warnings: [] };
      const itemSalesData = salesEntry.weeks;
      
      // Extract warnings (deduplicate)
      const uniqueWarnings = [...new Set(salesEntry.warnings)];
      const hasSOWarning = uniqueWarnings.length > 0;
      const soWarningDetails = uniqueWarnings.join("; ");

      let totalDemand = 0;
      Object.values(itemSalesData).forEach(w => totalDemand += w.qty);
      const avgWeeklyShipments = totalDemand > 0 ? (totalDemand / 13) : 0;

      // Beginning Inventory
      const beginningInventory = item.total_quantity_on_hand || 0;
      let currentInventory = beginningInventory;
      const weeks = [];

      for (let i = 0; i < 13; i++) {
        // Production (Work Orders)
        const prodData = productionMap[itemId]?.[i];
        const production = prodData ? prodData.qty : 0;
        const productionDetails = prodData ? prodData.details : [];

        // Shipments (Sales Orders)
        const salesData = itemSalesData[i];
        const shipments = salesData ? salesData.qty : 0;
        const shipmentDetails = salesData ? salesData.details : [];

        // THE MATH: Begin + Production - Shipments
        const ending = currentInventory + production - shipments;
        const hasIntraWeekNegative = ending < 0;

        weeks.push({
          production,
          productionDetails,
          shipments,
          shipmentDetails,
          ending,
          hasIntraWeekNegative
        });

        currentInventory = ending;
      }

      return {
        id: index + 1,
        sku,
        name,
        category,
        avgWeeklyShipments,
        beginningInventory,
        weeks,
        hasSOWarning,
        soWarningDetails
      };
    });
  };

  // Get unique categories - use actual data if available, otherwise fallback to mock data
  const categories = actualCategories.length > 0 ? actualCategories : [...new Set(finishedGoodsData.map(p => p.category))];
  
  // Stock status options
  const stockStatuses = [
    { id: 'critical', label: 'Critical (< 1 week)', color: 'text-red-700' },
    { id: 'low', label: 'Low (1-3 weeks)', color: 'text-yellow-700' },
    { id: 'good', label: 'Good (3-5 weeks)', color: 'text-green-700' },
    { id: 'overstocked', label: 'Overstocked (> 5 weeks)', color: 'text-blue-700' },
    { id: 'intraweek', label: 'Intra-week Negative', color: 'text-orange-700' }
  ];

  // Helper function to determine stock status
  const getStockStatus = (product) => {
    const hasIntraWeekNegative = product.weeks.some(w => w.hasIntraWeekNegative);
    if (hasIntraWeekNegative) return 'intraweek';
    
    const avgWeeksOnHand = product.weeks.reduce((sum, week) => {
      return sum + calculateWeeksOnHand(week.ending, product.avgWeeklyShipments);
    }, 0) / product.weeks.length;
    
    if (avgWeeksOnHand < 1) return 'critical';
    if (avgWeeksOnHand < 3) return 'low';
    if (avgWeeksOnHand <= 5) return 'good';
    return 'overstocked';
  };

  // Filter products
  const filteredProducts = finishedGoodsData.filter(product => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSKU = product.sku.toLowerCase().includes(search);
      const matchesName = product.name.toLowerCase().includes(search);
      if (!matchesSKU && !matchesName) return false;
    }
    
    // Category filter
    if (selectedCategories.length > 0) {
      if (!selectedCategories.includes(product.category)) return false;
    }
    
    // Stock status filter
    if (selectedStockStatus.length > 0) {
      const status = getStockStatus(product);
      if (!selectedStockStatus.includes(status)) return false;
    }

    // Zero rows filter
    if (hideZeroRows) {
      const hasActivity = product.beginningInventory !== 0 || product.weeks.some(w => w.production !== 0 || w.shipments !== 0);
      if (!hasActivity) return false;
    }
    
    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedStockStatus([]);
    setHideZeroRows(false);
  };

  // Toggle category selection
  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Toggle stock status selection
  const toggleStockStatus = (status) => {
    setSelectedStockStatus(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const getCellColor = (weeksOnHand, hasIntraWeekNegative = false) => {
    if (hasIntraWeekNegative) return "bg-orange-50 text-orange-900 border-2 border-orange-400"; // Warning for intra-week negative
    if (weeksOnHand < 1) return "bg-red-50 text-red-900";
    if (weeksOnHand < 3) return "bg-yellow-50 text-yellow-900";
    if (weeksOnHand <= 5) return "bg-green-50 text-green-900";
    return "bg-blue-50 text-blue-900"; // Overstocked
  };

  // Handle sales orders sync
  const handleRefreshSalesOrders = async () => {
    setSyncLoading(true);
    try {
      toast.info("Sales Order sync started...");
      await axios.post(`${API}/sync/sales-orders`);

      // Set a timeout to fetch data manually if WebSocket event doesn't arrive
      const timeoutId = setTimeout(async () => {
        try {
          const [salesOrdersResponse, workOrdersResponse, inventoryResponse] = await Promise.all([
            axios.get(`${API}/sales-orders`),
            axios.get(`${API}/work-orders`),
            axios.get(`${API}/items`)
          ]);

          const salesOrdersData = salesOrdersResponse.data;
          const workOrdersData = workOrdersResponse.data;
          const inventoryData = inventoryResponse.data;

          setSalesOrders(salesOrdersData);
          setWorkOrders(workOrdersData);
          setInventoryLevels(inventoryData);

          // Recalculate projections
          const calculatedData = calculateFinishedGoodsProjections(salesOrdersData, workOrdersData, inventoryData);
          setFinishedGoodsData(calculatedData);

          // Update categories
          const categories = [...new Set(calculatedData.map(item => item.category))].filter(Boolean);
          setActualCategories(categories);

          setSyncLoading(false);
          toast.success("Sales orders synced successfully");
        } catch (error) {
          console.error("Error fetching data after sync:", error);
          setSyncLoading(false);
        }
      }, 5000); // 5 second timeout

      // Store timeout ID to clear it if WebSocket event arrives first
      window.salesOrdersSyncTimeout = timeoutId;

    } catch (error) {
      console.error("Error syncing sales orders:", error);
      toast.error("Failed to sync sales orders from NetSuite");
      setSyncLoading(false);
    }
  };

  // Handle inventory refresh
  const handleRefreshInventory = async () => {
    setRefreshLoading(true);
    try {
      await axios.post(`${API}/sync/inventory-levels`);
      // The WebSocket will handle the UI update
    } catch (error) {
      console.error("Error refreshing inventory:", error);
      toast.error("Failed to refresh inventory levels");
    } finally {
      setRefreshLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Planning Data</h2>
          <p className="text-gray-600">Fetching items and sales orders...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">
            <X className="w-12 h-12 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Planning - Finished Goods</h1>
        <p className="text-sm text-gray-600 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Projected inventory: Beginning + Planned Production (Work Orders) - Planned Shipments (NetSuite Sales Orders)
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Showing projections for {finishedGoodsData.length} items based on live sales order data
        </p>
      </div>

      {/* Compact Filter Bar */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by SKU or Product Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Refresh Sales Orders Button */}
            <button
              onClick={handleRefreshSalesOrders}
              disabled={syncLoading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
              {syncLoading ? 'Syncing...' : 'Refresh Data from NetSuite'}
            </button>

            {/* Refresh Inventory Button */}
            <button
              onClick={handleRefreshInventory}
              disabled={refreshLoading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${refreshLoading ? 'animate-spin' : ''}`} />
              {refreshLoading ? 'Refreshing...' : 'Refresh Inventory'}
            </button>

            {/* Filters Button */}
            <button
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedCategories.length > 0 || selectedStockStatus.length > 0 || hideZeroRows) && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {selectedCategories.length + selectedStockStatus.length + (hideZeroRows ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Clear Filters Button */}
            {(searchTerm || selectedCategories.length > 0 || selectedStockStatus.length > 0 || hideZeroRows) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Active Filter Badges */}
          {(selectedCategories.length > 0 || selectedStockStatus.length > 0 || hideZeroRows) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {hideZeroRows && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-800 text-white text-sm rounded-full">
                  Hidden: All Zeros
                  <button onClick={() => setHideZeroRows(false)} className="hover:text-gray-300">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedCategories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {cat}
                  <button onClick={() => toggleCategory(cat)} className="hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedStockStatus.map(status => {
                const statusObj = stockStatuses.find(s => s.id === status);
                return (
                  <span key={status} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                    {statusObj?.label}
                    <button onClick={() => toggleStockStatus(status)} className="hover:text-gray-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Filter Summary */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredProducts.length} of {finishedGoodsData.length} products
          </div>
        </CardContent>
      </Card>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Filter Products</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                      selectedCategories.includes(category)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Zero Rows Filter */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="hideZeroRows"
                checked={hideZeroRows}
                onChange={(e) => setHideZeroRows(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="hideZeroRows" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                Hide rows with all zeros (no inventory or activity)
              </label>
            </div>

            {/* Stock Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Stock Status</label>
              <div className="space-y-2">
                {stockStatuses.map(status => (
                  <label key={status.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStockStatus.includes(status.id)}
                      onChange={() => toggleStockStatus(status.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                clearFilters();
                setShowFilterModal(false);
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilterModal(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Finished Goods - 13 Week Projection</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto h-[calc(100vh-280px)] border-b rounded-b-lg">
            <table className="w-full text-xs relative border-separate border-spacing-0">
              <thead className="bg-gray-100">
                <tr>
                  <th className="sticky top-0 z-50 bg-gray-100 text-left py-2 px-1 font-semibold text-gray-700 shadow-sm" style={{left: '0px', minWidth: '30px', width: '30px'}}></th>
                  {/* Merged SKU and Product Name into Item */}
                  <th className="sticky top-0 z-50 bg-gray-100 text-left py-2 px-2 font-semibold text-gray-700 border-r shadow-sm" style={{left: '30px', minWidth: '280px', width: '280px'}}>Item</th>
                  {/* Begin Renamed to On Hand */}
                  <th className="sticky top-0 z-50 bg-gray-100 text-center py-2 px-2 font-semibold text-gray-700 border-r bg-blue-50 shadow-sm" style={{left: '310px', minWidth: '80px', width: '80px'}}>On Hand</th>
                  {weekHeaders.map((weekDate, idx) => (
                    <th key={idx} className="sticky top-0 z-40 bg-gray-100 text-center py-3 px-1 font-semibold text-gray-700 border-r shadow-sm" style={{minWidth: '100px'}}>
                      {weekDate}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, idx) => (
                  <>
                    <tr 
                      key={product.id} 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} cursor-pointer hover:bg-blue-50`}
                      onClick={() => openDetailModal(product)}
                    >
                      <td className="sticky z-30 bg-inherit py-1 px-1" style={{left: '0px'}}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(product.id);
                          }}
                          className="hover:bg-gray-200 rounded p-0.5"
                        >
                          {expandedRows[product.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                      </td>
                      {/* Merged Item Cell */}
                      <td className="sticky z-30 bg-inherit py-2 px-2 border-r" style={{left: '30px'}}>
                        <div className="flex items-center gap-1">
                          <div className={`font-medium ${product.hasSOWarning ? 'text-orange-700' : 'text-gray-900'}`}>
                            {product.sku || product.name}
                          </div>
                          {product.hasSOWarning && (
                            <div title={product.soWarningDetails} className="cursor-help">
                              <AlertTriangle className="w-4 h-4 text-orange-500" />
                            </div>
                          )}
                        </div>
                      </td>
                      {/* On Hand Cell */}
                      <td className="sticky z-30 bg-inherit py-1 px-2 text-center font-semibold text-gray-900 border-r bg-blue-50" style={{left: '310px'}}>
                        {product.beginningInventory.toLocaleString()} <span className="text-xs text-gray-500">CS</span>
                      </td>
                      {product.weeks.map((week, weekIdx) => {
                        const weeksOnHand = calculateWeeksOnHand(week.ending, product.avgWeeklyShipments);
                        return (
                          <td key={weekIdx} className="py-1 px-1 border-r text-center">
                            <div className={`font-semibold text-xs px-2 py-1 rounded inline-block ${getCellColor(weeksOnHand, week.hasIntraWeekNegative)}`}>
                              {week.ending.toLocaleString()}
                              <div className="text-[10px] text-gray-600 mt-0.5">{weeksOnHand.toFixed(1)}w</div>
                              {week.hasIntraWeekNegative && <div className="text-[9px] text-orange-700 font-bold mt-0.5">⚠</div>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {expandedRows[product.id] && (
                      <tr className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="sticky z-30 bg-inherit" style={{left: '0px'}}></td>
                        {/* Adjusted colspan for new layout */}
                        <td className="sticky z-30 bg-inherit py-1 px-2 text-xs text-gray-600 border-r" style={{left: '30px'}} colSpan="2">
                          <div className="space-y-1 pl-2">
                            <div className="text-green-700 font-medium">+ Planned Production</div>
                            <div className="text-red-700 font-medium">- Planned Shipments</div>
                          </div>
                        </td>
                        {product.weeks.map((week, weekIdx) => (
                          <td key={weekIdx} className="py-1 px-1 border-r">
                            <div className="text-xs space-y-1 relative">
                              {/* ... (Keep existing Production/Shipments logic inside here) ... */}
                              <div className="text-green-700 group relative inline-block w-full">
                                <span className="cursor-help">+{week.production}</span>
                                {week.production > 0 && week.productionDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-48">
                                    {week.productionDetails.map((wo, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{wo.ref}</strong><br />
                                        {wo.qty} CS on Line {wo.line}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="text-red-700 group relative inline-block w-full">
                                <span className="cursor-help">-{week.shipments}</span>
                                {week.shipmentDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-48">
                                    {week.shipmentDetails.map((so, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{so.ref}</strong><br />
                                        {so.qty} CS to {so.customer}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        ))}
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-4 flex items-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-blue-50 border border-blue-200 rounded"></div>
          <span>Overstocked (5+ weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-green-50 border border-green-200 rounded"></div>
          <span>Good Stock (3-5 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-yellow-50 border border-yellow-200 rounded"></div>
          <span>Low Stock (1-3 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-red-50 border border-red-200 rounded"></div>
          <span>Critical (&lt;1 week)</span>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedProduct && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">{selectedProduct.sku} - {selectedProduct.name}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Inventory Chart with Weeks on Hand */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Inventory Position & Weeks on Hand</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={selectedProduct.weeks.map((w, idx) => ({ 
                      week: `W${idx + 1}`, 
                      inventory: w.ending,
                      weeksOnHand: calculateWeeksOnHand(w.ending, selectedProduct.avgWeeklyShipments)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis yAxisId="left" label={{ value: 'Inventory (CS)', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" label={{ value: 'Weeks on Hand', angle: 90, position: 'insideRight' }} />
                      <Tooltip />
                      <Line yAxisId="left" type="monotone" dataKey="inventory" stroke="#3b82f6" strokeWidth={2} name="Inventory" />
                      <Line yAxisId="right" type="monotone" dataKey="weeksOnHand" stroke="#10b981" strokeWidth={2} name="Weeks on Hand" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Weekly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedProduct.weeks.map((week, idx) => {
                      const weeksOnHand = calculateWeeksOnHand(week.ending, selectedProduct.avgWeeklyShipments);
                      
                      // Calculate dynamic start of week (Monday of current week + offset)
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const dayOfWeek = today.getDay(); 
                      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                      const planningStart = new Date(today);
                      planningStart.setDate(today.getDate() - daysToSubtract);
                      
                      const startOfWeek = new Date(planningStart);
                      startOfWeek.setDate(planningStart.getDate() + (idx * 7));
                      
                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const dayByDay = [];
                      
                      let runningBalance = idx === 0 ? selectedProduct.beginningInventory : selectedProduct.weeks[idx - 1].ending;
                      
                      // Create transactions using ACTUAL dates from the data
                      const transactions = [];
                      
                      week.productionDetails.forEach(wo => {
                        // Use actual date if available, otherwise fall back to Monday of that week
                        const date = wo.date ? new Date(wo.date) : new Date(startOfWeek);
                        // Fix timezone offset for YYYY-MM-DD strings to prevent off-by-one error
                        if (wo.date) {
                           date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
                        }
                        transactions.push({ ...wo, type: 'production', date });
                      });
                      
                      week.shipmentDetails.forEach(so => {
                        const date = so.date ? new Date(so.date) : new Date(startOfWeek);
                        if (so.date) {
                           date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
                        }
                        transactions.push({ ...so, type: 'shipment', date });
                      });
                      
                      transactions.sort((a, b) => a.date - b.date);
                      
                      // Generate day-by-day view
                      for (let d = 0; d < 7; d++) {
                        const currentDate = new Date(startOfWeek);
                        currentDate.setDate(startOfWeek.getDate() + d);
                        const dayTransactions = transactions.filter(t => t.date.toDateString() === currentDate.toDateString());
                        
                        dayByDay.push({
                          day: days[d],
                          date: currentDate,
                          transactions: dayTransactions,
                          startBalance: runningBalance
                        });
                        
                        // Update running balance for this day
                        dayTransactions.forEach(t => {
                          if (t.type === 'production') runningBalance += t.qty;
                          else runningBalance -= t.qty;
                        });
                      }
                      
                      return (
                        <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">Week {idx + 1}</h4>
                              <p className="text-xs text-gray-500">Day-by-Day Breakdown</p>
                            </div>
                            <div className="text-center">
                              <div className={`px-3 py-1 rounded font-semibold text-sm ${getCellColor(weeksOnHand)}`}>
                                {week.ending.toLocaleString()} CS
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-gray-700">
                                {weeksOnHand.toFixed(1)} Weeks on Hand
                              </div>
                              <div className="text-xs text-gray-500">
                                Avg: {selectedProduct.avgWeeklyShipments} CS/wk
                              </div>
                            </div>
                          </div>
                          
                          {/* Day-by-Day Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs border">
                              <thead className="bg-gray-200">
                                <tr>
                                  <th className="border px-2 py-1 text-left">Day</th>
                                  <th className="border px-2 py-1 text-left">Transaction</th>
                                  <th className="border px-2 py-1 text-right">Amount</th>
                                  <th className="border px-2 py-1 text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayByDay.map((day, dayIdx) => {
                                  let dayBalance = day.startBalance;
                                  const rows = [];
                                  
                                  if (day.transactions.length === 0) {
                                    rows.push(
                                      <tr key={`${dayIdx}-empty`} className="bg-white">
                                        <td className="border px-2 py-1 font-medium">{day.day} {day.date.getMonth()+1}/{day.date.getDate()}</td>
                                        <td className="border px-2 py-1 text-gray-400 italic" colSpan="2">No activity</td>
                                        <td className="border px-2 py-1 text-right font-medium">{dayBalance.toLocaleString()}</td>
                                      </tr>
                                    );
                                  } else {
                                    day.transactions.forEach((trans, transIdx) => {
                                      const isProduction = trans.type === 'production';
                                      const amount = isProduction ? trans.qty : -trans.qty;
                                      dayBalance += amount;
                                      
                                      rows.push(
                                        <tr key={`${dayIdx}-${transIdx}`} className={dayBalance < 0 ? 'bg-red-100' : 'bg-white'}>
                                          {transIdx === 0 && (
                                            <td className="border px-2 py-1 font-medium" rowSpan={day.transactions.length}>
                                              {day.day} {day.date.getMonth()+1}/{day.date.getDate()}
                                            </td>
                                          )}
                                          <td className={`border px-2 py-1 ${isProduction ? 'text-green-700' : 'text-red-700'}`}>
                                            <a href="#" className="text-blue-600 hover:underline font-medium">{trans.ref}</a>
                                            <span className="text-gray-600 ml-1">- {isProduction ? `Line ${trans.line}` : trans.customer}</span>
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-medium ${isProduction ? 'text-green-700' : 'text-red-700'}`}>
                                            {isProduction ? '+' : ''}{amount.toLocaleString()}
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-bold ${dayBalance < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                                            {dayBalance.toLocaleString()}
                                          </td>
                                        </tr>
                                      );
                                    });
                                  }
                                  
                                  return <Fragment key={`day-${dayIdx}`}>{rows}</Fragment>;
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PlanningFinishedGoodsPage;
